import React from 'react';
import styled from 'styled-components';
import {
  cssColorValue,
  screenMdMin,
  screenSmMin,
  semanticColors,
  spacer12,
  spacer24,
  spacer48,
  TextLink,
  Type,
  TypeList,
  TypeListItem,
} from '@spotify-internal/encore-web';

import { MastheadFooterData, MastheadLink } from 'lib/sanity/types';
import { urlForImage } from 'utils/sanity';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { isLinkVisible } from 'components/Masthead/shared/utils';
import { FooterTextLink } from 'components/Masthead/Footer/Link';

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${spacer48};
  width: 100%;

  @media (min-width: ${screenSmMin}) {
    grid-template-columns: 1fr 1fr 0.6fr;
    grid-gap: ${spacer24};
  }

  @media (min-width: ${screenMdMin}) {
    grid-template-columns: 1fr 1fr 2fr;
    flex: 0 1 58%;
  }
`;

const StyledListItem = styled(TypeListItem)`
  &:not(:last-of-type) {
    padding-block-end: ${spacer24};
  }
`;

const StyledListImage = styled(TypeListItem)`
  &:not(:last-of-type) {
    padding-block-end: ${spacer12};
  }
`;

const StyledImageLink = styled(TextLink)`
  && {
    &:focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: -2px;
      box-shadow: none;
      transition: none;
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }
  }
`;

const StyledImage = styled.img`
  display: block;
  max-inline-size: 100%;
`;

const LinkItem = ({ target, href, title, variant, image }: MastheadLink) => {
  const {
    verticalConfig: { verticalName },
  } = useServerSideConstants();
  if (variant === 'text') {
    return (
      <StyledListItem condensed>
        <FooterTextLink href={href} target={target}>
          {title}
        </FooterTextLink>
      </StyledListItem>
    );
  }

  if (variant === 'image' && image) {
    return (
      <StyledListImage condensed>
        <StyledImageLink href={href} target={target}>
          <StyledImage src={urlForImage({ source: image.asset, maxWidth: 64, vertical: verticalName })} alt={title} />
        </StyledImageLink>
      </StyledListImage>
    );
  }

  return null;
};

interface Props {
  footerEntry: MastheadFooterData;
}

export const FooterContent = ({ footerEntry }: Props) => {
  const { isLoggedIn } = useServerSideConstants();
  return (
    <StyledContainer>
      {footerEntry.columns.map(column => {
        return (
          <TypeList key={column.label} condensedAll listStyleReset>
            <StyledListItem condensed>
              <Type as="h2" variant="finaleBold" semanticColor="textSubdued">
                {column.label}
              </Type>
            </StyledListItem>
            {column.entries
              .filter(({ link }) => isLinkVisible(link, isLoggedIn))
              .map(entry => (
                <LinkItem key={entry.title} {...entry.link} />
              ))}
          </TypeList>
        );
      })}
    </StyledContainer>
  );
};
